import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { executeAdditionalPayment } from '../../services/payment.service';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const OrderPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);

  const hasExecutedRef = React.useRef(false);

  useEffect(() => {
    if (hasExecutedRef.current) return;
    
    const paymentId = searchParams.get('paymentId');
    const PayerID = searchParams.get('PayerID');
    const orderId = searchParams.get('orderId');
    const paymentIndex = searchParams.get('paymentIndex');

    if (!paymentId || !PayerID || !orderId || !paymentIndex) {
      setStatus('error');
      setError('Missing payment parameters. Your payment may not have completed.');
      return;
    }

    hasExecutedRef.current = true;

    executeAdditionalPayment({ paymentId, PayerID, orderId, paymentIndex: parseInt(paymentIndex, 10) })
      .then(data => {
        setOrderData(data);
        setStatus('success');
      })
      .catch(err => {
        setError(err.response?.data?.error || err.message || 'Payment verification failed.');
        setStatus('error');
      });
  }, [searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 font-['Poppins']">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-[#174568] mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Verifying your payment…</p>
          <p className="text-sm text-gray-400 mt-1">Please don't close this page.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 font-['Poppins']">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <XCircle size={56} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Payment Failed</h2>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <Link
            to="/clients/projects"
            className="inline-block bg-[#174568] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#10304a] transition-colors"
          >
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 font-['Poppins']">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
        <CheckCircle size={56} className="text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
        <p className="text-gray-500 text-sm mb-6">
          Your additional payment has been processed. The project timeline is updated.
        </p>

        {orderData?.transaction && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Amount paid:</span> ${parseFloat(orderData.transaction.amount).toFixed(2)} {orderData.transaction.currency}
            </p>
            {orderData.orderId && (
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-medium">Order ID:</span> {orderData.orderId.substring(orderData.orderId.length - 6).toUpperCase()}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/clients/projects"
            className="bg-[#174568] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#10304a] transition-colors"
          >
            View Projects
          </Link>
          {orderData?.orderId && (
            <Link
              to={`/clients/messages`} // Link to chat or general messages
              className="border border-[#174568] text-[#174568] px-6 py-2.5 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              Go to Messages
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderPaymentSuccess;
